/** Study-guide quiz banks for the Linear Algebra Systems and Eigenvalues guides — 15 MCQs per section. */

export const LA_S_INTRO_QUIZ = [
  {
    prompt: "Solve the system $x+y=5$, $x-y=1$.",
    options: ["$x=2,\\ y=3$", "$x=3,\\ y=2$", "$x=4,\\ y=1$"],
    answer: "B",
    explanation: "Adding gives $2x=6$, so $x=3$ and $y=2$.",
  },
  {
    prompt: "Which equation is linear in $x$ and $y$?",
    options: ["$2x-3y=7$", "$xy=4$", "$x^2+y=1$"],
    answer: "A",
    explanation: "A linear equation has no products or powers of unknowns.",
  },
  {
    prompt: "The system $x+y=1$, $2x+2y=3$ has:",
    options: ["Infinitely many solutions", "A unique solution", "No solution"],
    answer: "C",
    explanation: "The second row is inconsistent with twice the first.",
  },
  {
    prompt: "The homogeneous system $A\\mathbf{x}=\\mathbf{0}$ always has:",
    options: ["No solution", "At least the trivial solution $\\mathbf{x}=\\mathbf{0}$", "Exactly one nonzero solution"],
    answer: "B",
    explanation: "Substituting $\\mathbf{0}$ always works.",
  },
  {
    prompt: "Write $x-y=4$, $2x+y=5$ as $A\\mathbf{x}=\\mathbf{b}$. Then $A$ is:",
    options: ["$\\begin{pmatrix}1&2\\\\-1&1\\end{pmatrix}$", "$\\begin{pmatrix}4\\\\5\\end{pmatrix}$", "$\\begin{pmatrix}1&-1\\\\2&1\\end{pmatrix}$"],
    answer: "C",
    explanation: "Coefficient matrix of the unknowns.",
  },
  {
    prompt: "Solve $2x=6$, $y=-1$.",
    options: ["$x=3,\\ y=-1$", "$x=6,\\ y=-1$", "$x=3,\\ y=1$"],
    answer: "A",
    explanation: "Divide the first equation by $2$.",
  },
  {
    prompt: "An augmented matrix for $x+2y=3$, $4x-y=1$ is:",
    options: ["$\\begin{pmatrix}1&2&3\\\\4&-1&1\\end{pmatrix}$", "$\\begin{pmatrix}1&2\\\\4&-1\\end{pmatrix}$", "$\\begin{pmatrix}3&1\\\\1&4\\end{pmatrix}$"],
    answer: "A",
    explanation: "Coefficients plus the right-hand side column.",
  },
  {
    prompt: "The system $x+y+z=0$, $x-y=0$, $y-z=0$ has:",
    options: ["No solution", "Only the trivial solution", "A line of solutions"],
    answer: "B",
    explanation: "Forces $x=y=z=0$.",
  },
  {
    prompt: "If two distinct lines in the plane intersect at exactly one point, the $2\\times 2$ system has:",
    options: ["Infinitely many solutions", "No solution", "A unique solution"],
    answer: "C",
    explanation: "One intersection point means one solution pair.",
  },
  {
    prompt: "Solve $x+2y=4$, $3x+6y=12$.",
    options: ["No solution", "Infinitely many solutions", "The unique solution $x=4$, $y=0$"],
    answer: "B",
    explanation: "The second equation is three times the first.",
  },
  {
    prompt: "Consistency of a linear system means:",
    options: ["The solution is unique", "All variables are free", "At least one solution exists"],
    answer: "C",
    explanation: "Consistent means nonempty solution set.",
  },
  {
    prompt: "For $x-y=2$, $x-y=5$, the solution set is:",
    options: ["Empty", "A single point", "A line"],
    answer: "A",
    explanation: "Contradictory parallel equations.",
  },
  {
    prompt: "Solve $x=1$, $y=2$, $z=3$ written as three equations. The solution is:",
    options: ["$(0,0,0)$", "Infinitely many triples", "$(1,2,3)$"],
    answer: "C",
    explanation: "Each variable is already determined.",
  },
  {
    prompt: "A $3\\times 3$ system with a unique solution has coefficient matrix that is:",
    options: ["Invertible", "The zero matrix", "Singular"],
    answer: "A",
    explanation: "Unique solution iff $A$ is invertible.",
  },
  {
    prompt: "The equation $0\\cdot x+0\\cdot y=1$ in a system forces:",
    options: ["The trivial solution", "Inconsistency", "A free variable only"],
    answer: "B",
    explanation: "A false statement $0=1$ appears.",
  },

  {
    prompt: "In the matrix equation $Ax=b$, the columns of $A$ are weighted by the entries of:",
    options: ["$x$", "$b$", "The determinant"],
    answer: "A",
    explanation: "Matrix-vector multiplication forms a linear combination of the columns using the components of $x$.",
  },
  {
    prompt: "A homogeneous system $Ax=0$ always has:",
    options: ["No solution", "The trivial solution $x=0$", "Exactly two solutions"],
    answer: "B",
    explanation: "Substituting $x=0$ always satisfies the homogeneous system.",
  },
  {
    prompt: "If $A$ is $5\\times3$, then $Ax=b$ represents:",
    options: ["3 equations in 5 unknowns", "15 equations in 1 unknown", "5 equations in 3 unknowns"],
    answer: "C",
    explanation: "Rows correspond to equations and columns to unknowns.",
  },
  {
    prompt: "A system is inconsistent when:",
    options: ["No vector $x$ satisfies all equations", "There is exactly one solution", "There is a free variable"],
    answer: "A",
    explanation: "Inconsistency means the solution set is empty.",
  },
  {
    prompt: "If $Ax=b$ has two distinct solutions, then it must have:",
    options: ["Exactly two solutions", "Infinitely many solutions", "No other solutions"],
    answer: "B",
    explanation: "For a linear system, the line through two solutions in a nullspace direction yields infinitely many solutions.",
  },
];

export const LA_S_GAUSS_QUIZ = [
  {
    prompt: "Which is an elementary row operation?",
    options: ["Swap two rows", "Square every entry", "Delete a row"],
    answer: "A",
    explanation: "The three elementary operations include row swaps.",
  },
  {
    prompt: "A pivot position in a row is:",
    options: ["Any zero entry", "The last column always", "The first nonzero entry in that row of an echelon form"],
    answer: "C",
    explanation: "Pivots lead the nonzero rows in echelon form.",
  },
  {
    prompt: "The row $[0\\ 0\\ 0\\mid 5]$ in an augmented matrix means the system is:",
    options: ["Homogeneous", "Inconsistent", "Underdetermined only"],
    answer: "B",
    explanation: "It asserts $0=5$.",
  },
  {
    prompt: "Back-substitute from $\\begin{pmatrix}1&2&3\\\\0&1&4\\end{pmatrix}$: if variables are $x,y$, then:",
    options: ["$y=0$, $x=3$", "$y=4$, $x=3$", "$y=4$, $x=3-8=-5$"],
    answer: "C",
    explanation: "From $y=4$ and $x+2y=3$ get $x=-5$.",
  },
  {
    prompt: "Reduced row echelon form requires pivots to be $1$ and:",
    options: ["All entries above pivots nonzero", "The only nonzero entry in their columns", "A square matrix"],
    answer: "B",
    explanation: "RREF clears above and below each pivot.",
  },
  {
    prompt: "How many free variables does $\\begin{pmatrix}1&2&0\\\\0&0&1\\end{pmatrix}$ (coefficient part) suggest for three unknowns?",
    options: ["$1$", "$2$", "$0$"],
    answer: "A",
    explanation: "Two pivot columns leave one free variable.",
  },
  {
    prompt: "Row-reducing $\\begin{pmatrix}1&1&2\\\\2&2&4\\end{pmatrix}$ can produce:",
    options: ["A pivot in every column", "$\\begin{pmatrix}1&0&0\\\\0&1&0\\end{pmatrix}$", "$\\begin{pmatrix}1&1&2\\\\0&0&0\\end{pmatrix}$"],
    answer: "C",
    explanation: "The second row is a multiple of the first.",
  },
  {
    prompt: "An echelon form never has a nonzero entry:",
    options: ["On the diagonal", "Below a pivot in the same column", "Above a pivot"],
    answer: "B",
    explanation: "Zeros below pivots define row echelon form.",
  },
  {
    prompt: "Starting from $\\begin{pmatrix}2&4&6\\\\1&1&1\\end{pmatrix}$, a useful first step is:",
    options: ["Swap rows or scale the first row", "Delete the second row", "Add a new variable"],
    answer: "A",
    explanation: "Create a convenient pivot, often $1$.",
  },
  {
    prompt: "If the only nonzero row of a $2\\times 3$ coefficient echelon form is $[1\\ 2\\ 3]$, the number of free variables is:",
    options: ["$2$", "$1$", "$0$"],
    answer: "A",
    explanation: "One pivot among three columns.",
  },
  {
    prompt: "Back substitution on $x+y=5$, $y=2$ gives:",
    options: ["$x=5$", "$x=3$", "$x=2$"],
    answer: "B",
    explanation: "$x+2=5$ so $x=3$.",
  },
  {
    prompt: "Replacing $R_2$ by $R_2-3R_1$ is:",
    options: ["A column operation", "Not allowed in Gaussian elimination", "An elementary row operation"],
    answer: "C",
    explanation: "Adding a multiple of one row to another is elementary.",
  },
  {
    prompt: "A pivot cannot occur in a column that is:",
    options: ["The first column", "Entirely zero in the working matrix", "The last column of a square matrix"],
    answer: "B",
    explanation: "All-zero columns have no pivot.",
  },
  {
    prompt: "After elimination, $\\begin{pmatrix}1&0&2\\\\0&1&-1\\\\0&0&0\\end{pmatrix}\\mid\\begin{pmatrix}3\\\\4\\\\0\\end{pmatrix}$ describes:",
    options: ["A unique solution", "No solution", "A line of solutions"],
    answer: "C",
    explanation: "One free variable with a consistent zero row.",
  },
  {
    prompt: "Counting roughly, eliminating an $n\\times n$ system with dense Gaussian elimination costs on the order of:",
    options: ["$n^3$ arithmetic operations", "$n$ operations", "$2^n$ operations"],
    answer: "A",
    explanation: "Classic cubic complexity of Gaussian elimination.",
  },

  {
    prompt: "A pivot in echelon form is:",
    options: ["Any zero below a row", "The leading nonzero entry of a nonzero row", "The rightmost entry only"],
    answer: "B",
    explanation: "Pivot positions organize elimination and identify basic variables.",
  },
  {
    prompt: "In RREF, every pivot must equal:",
    options: ["$0$", "The determinant", "$1$"],
    answer: "C",
    explanation: "Reduced row echelon form scales each pivot to one.",
  },
  {
    prompt: "In RREF, a pivot is the only nonzero entry in its:",
    options: ["Column", "Row", "Matrix"],
    answer: "A",
    explanation: "Entries above and below every pivot are zero.",
  },
  {
    prompt: "A free variable corresponds to a column with:",
    options: ["Two pivots", "No pivot", "A pivot in every row"],
    answer: "B",
    explanation: "Nonpivot variable columns correspond to free parameters.",
  },
  {
    prompt: "Elementary row operations preserve the:",
    options: ["Individual matrix entries", "Determinant exactly in every case", "Solution set of the associated system"],
    answer: "C",
    explanation: "They produce equivalent systems, though determinant may change under swaps/scaling.",
  },
];

export const LA_S_RANK_QUIZ = [
  {
    prompt: "The rank of $\\begin{pmatrix}1&0\\\\0&1\\end{pmatrix}$ is:",
    options: ["$1$", "$2$", "$0$"],
    answer: "B",
    explanation: "Two pivot positions.",
  },
  {
    prompt: "Rank-nullity for an $m\\times n$ matrix $A$ says:",
    options: ["$\\operatorname{rank}A+\\operatorname{nullity}A=n$", "$\\operatorname{rank}A+\\operatorname{nullity}A=m$", "$\\operatorname{rank}A=\\operatorname{nullity}A$"],
    answer: "A",
    explanation: "Nullity counts free variables among $n$ unknowns.",
  },
  {
    prompt: "If $\\operatorname{rank}A=2$ and $\\operatorname{rank}[A\\mid\\mathbf{b}]=3$, the system $A\\mathbf{x}=\\mathbf{b}$ is:",
    options: ["Consistent with unique solution", "Consistent with free variables", "Inconsistent"],
    answer: "C",
    explanation: "Augmented rank larger than $\\operatorname{rank}A$ means inconsistency.",
  },
  {
    prompt: "Nullity of a $3\\times 3$ invertible matrix is:",
    options: ["$3$", "$1$", "$0$"],
    answer: "C",
    explanation: "Full rank implies trivial nullspace.",
  },
  {
    prompt: "Dimension of the column space equals:",
    options: ["The rank", "The nullity", "The number of rows always"],
    answer: "A",
    explanation: "Rank is $\\dim\\operatorname{Col}A$.",
  },
  {
    prompt: "For $A=\\begin{pmatrix}1&2&3\\\\0&0&0\\end{pmatrix}$, $\\operatorname{rank}A$ equals:",
    options: ["$2$", "$1$", "$3$"],
    answer: "B",
    explanation: "One nonzero row after echelon thoughts.",
  },
  {
    prompt: "Number of free variables equals:",
    options: ["$m-\\operatorname{rank}A$ always", "$\\operatorname{rank}A$", "$n-\\operatorname{rank}A$"],
    answer: "C",
    explanation: "Nullity $=n-\\operatorname{rank}$.",
  },
  {
    prompt: "If $\\operatorname{rank}A=\\operatorname{rank}[A\\mid\\mathbf{b}]=2$ for a $3\\times 4$ matrix, then:",
    options: ["The system is consistent with $2$ free variables", "The system is inconsistent", "There is a unique solution"],
    answer: "A",
    explanation: "Consistency holds; nullity $=4-2=2$.",
  },
  {
    prompt: "The rank of the zero $2\\times 2$ matrix is:",
    options: ["$2$", "$0$", "$1$"],
    answer: "B",
    explanation: "No pivots.",
  },
  {
    prompt: "If columns of $A$ are independent and $A$ is $3\\times 2$, then $\\operatorname{rank}A$ is:",
    options: ["$2$", "$3$", "$1$"],
    answer: "A",
    explanation: "Two independent columns give rank $2$.",
  },
  {
    prompt: "Dimension of $\\operatorname{Nul}A$ for $A=\\begin{pmatrix}1&1\\\\2&2\\end{pmatrix}$ is:",
    options: ["$0$", "$1$", "$2$"],
    answer: "B",
    explanation: "Rank $1$ and $n=2$ so nullity $1$.",
  },
  {
    prompt: "Reading rank from echelon form means counting:",
    options: ["Total rows including zeros", "The right-hand side entries", "Nonzero rows (pivots)"],
    answer: "C",
    explanation: "Each pivot row contributes one to the rank.",
  },
  {
    prompt: "For a consistent system, $\\operatorname{rank}A$ equals:",
    options: ["Always $m$", "$n$", "$\\operatorname{rank}[A\\mid\\mathbf{b}]$"],
    answer: "C",
    explanation: "Consistency criterion.",
  },
  {
    prompt: "$\\operatorname{rank}\\begin{pmatrix}0&1&0\\\\0&0&1\\\\0&0&0\\end{pmatrix}$ equals:",
    options: ["$2$", "$3$", "$1$"],
    answer: "A",
    explanation: "Two pivot columns.",
  },
  {
    prompt: "If $\\operatorname{rank}A=n$ for an $n\\times n$ matrix, then $A\\mathbf{x}=\\mathbf{b}$ has:",
    options: ["No solution for some $\\mathbf{b}$", "A unique solution for every $\\mathbf{b}$", "Infinitely many solutions always"],
    answer: "B",
    explanation: "Full rank square matrices are invertible.",
  },

  {
    prompt: "A system $Ax=b$ is consistent exactly when:",
    options: ["$\\operatorname{rank}(A)=0$", "$\\operatorname{rank}(A)>n$", "$\\operatorname{rank}(A)=\\operatorname{rank}([A\\mid b])$"],
    answer: "C",
    explanation: "The augmented column must not create an additional pivot.",
  },
  {
    prompt: "For a consistent system with $n$ unknowns and rank $r$, the number of free variables is:",
    options: ["$n-r$", "$r-n$", "$n+r$"],
    answer: "A",
    explanation: "Each nonpivot variable is free, giving nullity $n-r$.",
  },
  {
    prompt: "If $A$ is $3\\times3$ with rank $2$, its nullity is:",
    options: ["$2$", "$1$", "$3$"],
    answer: "B",
    explanation: "Rank-nullity gives $3=2+\\operatorname{nullity}(A)$.",
  },
  {
    prompt: "If $\\operatorname{rank}(A)<\\operatorname{rank}([A\\mid b])$, then $Ax=b$ is:",
    options: ["Uniquely solvable", "Homogeneous", "Inconsistent"],
    answer: "C",
    explanation: "The augmented matrix contains a contradiction pivot not present in $A$.",
  },
  {
    prompt: "A square $n\\times n$ matrix has a unique solution for every $b$ exactly when its rank is:",
    options: ["$n$", "$n-1$", "$0$"],
    answer: "A",
    explanation: "Full rank makes the square matrix invertible.",
  },
];

export const LA_S_SUBSPACES_QUIZ = [
  {
    prompt: "Which condition is required for a nonempty subset $W$ of $\\mathbb{R}^n$ to be a subspace?",
    options: ["Closed under vector addition and scalar multiplication", "Contains exactly one basis", "Every vector has length $1$"],
    answer: "A",
    explanation: "Together with containing the zero vector, closure under addition and scalar multiplication gives the standard subspace test.",
  },
  {
    prompt: "The plane $x+2y-z=0$ in $\\mathbb{R}^3$ is:",
    options: ["Not a subspace because it is two-dimensional", "A subspace", "Not a subspace because it contains the origin"],
    answer: "B",
    explanation: "It is the solution set of a homogeneous linear equation, so it contains $0$ and is closed under linear combinations.",
  },
  {
    prompt: "The plane $x+2y-z=1$ is not a subspace because:",
    options: ["It contains too many vectors", "Its normal vector is nonzero", "It does not contain the zero vector"],
    answer: "C",
    explanation: "Subspaces must contain the origin; substituting $(0,0,0)$ gives $0\\neq1$.",
  },
  {
    prompt: "For an $m\\times n$ matrix $A$, $\\operatorname{Col}(A)$ is a subspace of:",
    options: ["$\\mathbb{R}^n$", "$\\mathbb{R}^m$", "$\\mathbb{R}^{m+n}$"],
    answer: "B",
    explanation: "Each column and every output $Ax$ has $m$ entries.",
  },
  {
    prompt: "For an $m\\times n$ matrix $A$, $\\operatorname{Row}(A)$ is a subspace of:",
    options: ["$\\mathbb{R}^m$", "$\\mathbb{R}^{mn}$", "$\\mathbb{R}^n$"],
    answer: "C",
    explanation: "Every row has $n$ entries.",
  },
  {
    prompt: "A basis for $\\operatorname{Col}(A)$ should be taken from:",
    options: ["The pivot columns of the original matrix $A$", "The pivot columns of the RREF only", "The free columns of the RREF"],
    answer: "A",
    explanation: "Row reduction locates pivot columns, but it changes the column space, so the basis columns must come from the original matrix.",
  },
  {
    prompt: "A convenient basis for $\\operatorname{Row}(A)$ is given by:",
    options: ["The zero rows of RREF", "The original pivot columns", "The nonzero rows of an echelon form of $A$"],
    answer: "C",
    explanation: "Elementary row operations preserve the row space, and the nonzero echelon rows are independent.",
  },
  {
    prompt: "The left null space of $A$ is:",
    options: ["$\\operatorname{Nul}(A)$", "$\\operatorname{Nul}(A^T)$", "$\\operatorname{Col}(A)$"],
    answer: "B",
    explanation: "By definition, the left null space consists of vectors $y$ with $A^Ty=0$.",
  },
  {
    prompt: "If $A$ is $5\\times 8$ with rank $3$, then $\\dim\\operatorname{Nul}(A)$ is:",
    options: ["$5$", "$3$", "$2$"],
    answer: "A",
    explanation: "Rank–nullity gives $8-3=5$.",
  },
  {
    prompt: "If $A$ is $5\\times 8$ with rank $3$, then $\\dim\\operatorname{Nul}(A^T)$ is:",
    options: ["$5$", "$3$", "$2$"],
    answer: "C",
    explanation: "The left-null dimension is $m-r=5-3=2$.",
  },
  {
    prompt: "Which identity relates the row space to the null space?",
    options: ["$\\operatorname{Row}(A)^\\perp=\\operatorname{Col}(A)$", "$\\operatorname{Row}(A)^\\perp=\\operatorname{Nul}(A)$", "$\\operatorname{Nul}(A)^\\perp=\\operatorname{Nul}(A^T)$"],
    answer: "B",
    explanation: "A vector is in the null space exactly when it is orthogonal to every row of $A$.",
  },
  {
    prompt: "Which identity relates the column space to the left null space?",
    options: ["$\\operatorname{Col}(A)^\\perp=\\operatorname{Nul}(A^T)$", "$\\operatorname{Col}(A)^\\perp=\\operatorname{Row}(A)$", "$\\operatorname{Col}(A)=\\operatorname{Nul}(A^T)$"],
    answer: "A",
    explanation: "Left-null vectors are orthogonal to every column of $A$.",
  },
  {
    prompt: "If $A$ has $n$ columns and rank $n$, rank–nullity implies:",
    options: ["$\\operatorname{nullity}(A)=n$", "$A$ must have more columns than rows", "$\\operatorname{Nul}(A)=\\{0\\}$"],
    answer: "C",
    explanation: "Nullity is $n-n=0$, so the homogeneous system has only the trivial solution.",
  },
  {
    prompt: "For a consistent system $Ax=b$, if $\\operatorname{nullity}(A)=2$, the solution set has:",
    options: ["Exactly two points", "Two independent free directions", "No free variables"],
    answer: "B",
    explanation: "Every solution is one particular solution plus the two-dimensional null space.",
  },
  {
    prompt: "The pivot/free-variable proof of rank–nullity works because $r$ pivots among $n$ columns leave:",
    options: ["$n-r$ free variables", "$m-r$ free variables", "$r-n$ free variables"],
    answer: "A",
    explanation: "The remaining $n-r$ non-pivot columns correspond to free variables, so nullity is $n-r$.",
  },
];


export const LA_S_GEO_QUIZ = [
  {
    prompt: "The solution set of a consistent $2\\times 2$ system with one free variable is geometrically:",
    options: ["A line", "A single point", "A plane"],
    answer: "A",
    explanation: "One parameter yields a line in the plane.",
  },
  {
    prompt: "Two distinct parallel lines in $\\mathbb{R}^2$ as equations give:",
    options: ["Exactly one solution", "No common solution", "Infinitely many solutions"],
    answer: "B",
    explanation: "Parallel distinct lines never meet.",
  },
  {
    prompt: "If two planes in $\\mathbb{R}^3$ intersect and are not coincident, their intersection is typically:",
    options: ["All of $\\mathbb{R}^3$", "A single point", "A line"],
    answer: "C",
    explanation: "Two independent plane equations leave one free variable.",
  },
  {
    prompt: "A particular solution $\\mathbf{x}_p$ plus the nullspace describes:",
    options: ["Only homogeneous solutions", "Only inconsistent systems", "The full solution set of $A\\mathbf{x}=\\mathbf{b}$"],
    answer: "C",
    explanation: "Affine translate of the homogeneous solution space.",
  },
  {
    prompt: "Parametric form $\\mathbf{x}=\\begin{pmatrix}1\\\\0\\end{pmatrix}+t\\begin{pmatrix}2\\\\1\\end{pmatrix}$ is:",
    options: ["A plane", "A line through $(1,0)$", "Only the origin"],
    answer: "B",
    explanation: "Point plus span of one direction vector.",
  },
  {
    prompt: "Three planes with no common point correspond to:",
    options: ["An inconsistent $3\\times 3$ system", "A line of solutions", "A unique solution"],
    answer: "A",
    explanation: "Empty intersection means no solution.",
  },
  {
    prompt: "Coincident lines (same line twice) as a $2\\times 2$ system give:",
    options: ["Infinitely many solutions", "No solution", "Exactly two solutions"],
    answer: "A",
    explanation: "Redundant equations describe the same line.",
  },
  {
    prompt: "A singular coefficient matrix geometrically often means:",
    options: ["Always a unique intersection point", "Parallel or dependent constraint hyperplanes", "Orthogonal rows"],
    answer: "B",
    explanation: "Dependent rows fail to cut down dimension fully.",
  },
  {
    prompt: "The homogeneous solution set always contains:",
    options: ["No vectors", "Exactly one nonzero vector", "The origin"],
    answer: "C",
    explanation: "It is a subspace.",
  },
  {
    prompt: "Solution set $\\{\\mathbf{0}\\}$ for $A\\mathbf{x}=\\mathbf{0}$ means columns of $A$ are:",
    options: ["Dependent", "Parallel to $\\mathbf{b}$", "Independent (trivial nullspace)"],
    answer: "C",
    explanation: "Only the zero combination works.",
  },
  {
    prompt: "In $\\mathbb{R}^3$, one linear equation typically describes:",
    options: ["A point", "A plane", "A line"],
    answer: "B",
    explanation: "One constraint leaves two free variables.",
  },
  {
    prompt: "If $\\mathbf{x}=\\mathbf{x}_p+\\mathbf{v}_h$ with $A\\mathbf{v}_h=\\mathbf{0}$, then $A\\mathbf{x}$ equals:",
    options: ["$\\mathbf{b}$ whenever $A\\mathbf{x}_p=\\mathbf{b}$", "$\\mathbf{v}_h$", "$\\mathbf{0}$"],
    answer: "A",
    explanation: "Linearity: $A\\mathbf{x}_p+A\\mathbf{v}_h=\\mathbf{b}$.",
  },
  {
    prompt: "Intersecting $x+y+z=1$ and $x+y+z=2$ in $\\mathbb{R}^3$ yields:",
    options: ["A line", "A plane", "The empty set"],
    answer: "C",
    explanation: "Parallel distinct planes.",
  },
  {
    prompt: "A unique solution of a $3\\times 3$ system is a:",
    options: ["Single point in $\\mathbb{R}^3$", "Plane", "Line"],
    answer: "A",
    explanation: "Zero free variables.",
  },
  {
    prompt: "Parametric vector form with two free parameters in $\\mathbb{R}^3$ describes:",
    options: ["A line", "A plane (through a particular point)", "Only a point"],
    answer: "B",
    explanation: "Two independent directions span a plane.",
  },

  {
    prompt: "The solution set of a consistent homogeneous system is:",
    options: ["An affine set never containing the origin", "A subspace through the origin", "Always a single point"],
    answer: "B",
    explanation: "Null spaces are subspaces and therefore contain the origin.",
  },
  {
    prompt: "The solution set of a consistent nonhomogeneous system $Ax=b$ can be written as:",
    options: ["$\\operatorname{Col}(A)+b$", "$A^{-1}$ even when $A$ is singular", "$x_p+\\operatorname{Nul}(A)$"],
    answer: "C",
    explanation: "Every solution differs from a particular solution by a nullspace vector.",
  },
  {
    prompt: "If a consistent system in three unknowns has nullity $1$, its solution set is geometrically:",
    options: ["An affine line", "An affine plane", "A single point"],
    answer: "A",
    explanation: "One free parameter traces a line.",
  },
  {
    prompt: "If a consistent system in three unknowns has nullity $2$, its solution set is geometrically:",
    options: ["An affine line", "An affine plane", "A single point"],
    answer: "B",
    explanation: "Two independent free parameters span a plane of directions.",
  },
  {
    prompt: "A unique solution corresponds to nullity:",
    options: ["$1$", "$n$", "$0$"],
    answer: "C",
    explanation: "No free directions remain when the null space contains only zero.",
  },
];


export const LA_S_LU_QUIZ = [
  {
    prompt: "In A = LU, what kind of matrix is U?",
    options: ["Upper triangular", "Lower triangular", "Diagonal", "Orthogonal"],
    answer: "A",
    explanation: "U is upper triangular — it's the result of forward elimination on A, assuming no row swaps were needed.",
  },
  {
    prompt: "What goes on the diagonal of L?",
    options: ["The pivots of A", "1's", "The eigenvalues of A", "0's"],
    answer: "B",
    explanation: "By convention L is unit lower triangular — its diagonal entries are always 1.",
  },
  {
    prompt: "What do the below-diagonal entries of L represent?",
    options: [
      "Random placeholder values",
      "The eigenvalues of A",
      "The multipliers used to eliminate each entry during forward elimination",
      "The entries of U reversed",
    ],
    answer: "C",
    explanation: "Each below-diagonal entry of L records the multiplier used in the row operation that cleared that position.",
  },
  {
    prompt: "Why factor A = LU before solving Ax = b for several different values of b?",
    options: [
      "It changes the solution set to something simpler",
      "It makes A invertible even if it wasn't before",
      "Elimination is done once; each new b only needs two cheap triangular solves",
      "It avoids needing b at all",
    ],
    answer: "C",
    explanation: "Forming L and U is the expensive step; once done, each new b costs only a forward and a back substitution.",
  },
  {
    prompt: "Solving Ly = b is done by:",
    options: ["Back substitution, bottom to top", "Forward substitution, top to bottom", "Matrix inversion", "Cramer's Rule"],
    answer: "B",
    explanation: "Since L is lower triangular, its first row involves only the first unknown, letting you solve top to bottom.",
  },
  {
    prompt: "Once y is known from Ly = b, how do you find x?",
    options: [
      "Solve Ux = y by back substitution, bottom to top",
      "Solve Ux = y by forward substitution, top to bottom",
      "x equals y directly",
      "Multiply y by U",
    ],
    answer: "A",
    explanation: "U is upper triangular, so its last row involves only the last unknown, letting you solve bottom to top.",
  },
  {
    prompt: "If a needed pivot is zero during elimination, what's the correct fix?",
    options: [
      "Set that pivot to 1 and continue",
      "Give up — no factorization exists",
      "Swap rows and track it with a permutation matrix P, giving PA = LU",
      "Swap columns instead of rows",
    ],
    answer: "C",
    explanation: "A permutation matrix P records the row swap; the reordered matrix PA then factors cleanly as LU.",
  },
  {
    prompt: "Why is LU factorization especially useful in engineering simulations with many load cases?",
    options: [
      "Each load case needs a full new elimination anyway",
      "The same A's expensive elimination is reused, and only the cheap triangular solves repeat per case",
      "It removes the need for a coefficient matrix",
      "It only works for one load case at a time",
    ],
    answer: "B",
    explanation: "Factor once; every new right-hand side (load case) only needs the two fast triangular solves.",
  },
  {
    prompt: "Multiplying L and U back together should give you:",
    options: ["The identity matrix", "The original matrix A (or PA if rows were swapped)", "The zero matrix", "U squared"],
    answer: "B",
    explanation: "LU is a factorization of A, so recombining L and U must reconstruct A exactly (or PA if a permutation was used).",
  },
  {
    prompt: "For $A=\\begin{pmatrix}2&1\\4&3\\end{pmatrix}$, the first elimination multiplier is:",
    options: ["$2$", "$1/2$", "$4$", "$3$"],
    answer: "A",
    explanation: "Use $R_2\\leftarrow R_2-2R_1$, so the multiplier stored in $L_{21}$ is $2$.",
  },
  {
    prompt: "If $A=LU$ and $L$ and $U$ are both invertible, then $A^{-1}$ equals:",
    options: ["$U^{-1}L^{-1}$", "$L^{-1}U^{-1}$", "$LU^{-1}$", "$L^TU$"],
    answer: "A",
    explanation: "The inverse of a product reverses order: $(LU)^{-1}=U^{-1}L^{-1}$.",
  },
  {
    prompt: "A unit lower-triangular matrix $L$ has determinant:",
    options: ["$1$", "$0$", "The product of elimination multipliers", "Always $-1$"],
    answer: "A",
    explanation: "The determinant of a triangular matrix is the product of diagonal entries; every diagonal entry of unit $L$ is $1$.",
  },
  {
    prompt: "Which factor contains the pivot values produced during elimination?",
    options: ["$U$", "$L$ only", "$P$ only", "Neither $L$ nor $U$"],
    answer: "A",
    explanation: "$U$ is the upper-triangular matrix produced by forward elimination, so its diagonal contains the resulting pivots.",
  },
  {
    prompt: "Why is solving triangular systems cheaper than repeating Gaussian elimination?",
    options: ["Forward/back substitution needs about $O(n^2)$ work instead of $O(n^3)$ elimination", "Triangular systems need no arithmetic", "They always have integer solutions", "They avoid storing matrices"],
    answer: "A",
    explanation: "Once the factorization is available, each new right-hand side requires only forward and back substitution.",
  },
  {
    prompt: "If row swaps are required, the standard pivoted LU relationship is:",
    options: ["$PA=LU$", "$AP=LU$", "$A=PLU^{-1}$", "$P=L+U$"],
    answer: "A",
    explanation: "The permutation matrix $P$ records the row reordering, giving $PA=LU$.",
  },

  {
    prompt: "In $A=LU$, the matrix $L$ is typically:",
    options: ["Upper triangular", "Diagonal with zeros only", "Lower triangular"],
    answer: "C",
    explanation: "Gaussian elimination multipliers are stored below the diagonal in $L$.",
  },
  {
    prompt: "In $A=LU$, the matrix $U$ is:",
    options: ["Upper triangular", "Lower triangular", "Orthogonal in every case"],
    answer: "A",
    explanation: "$U$ is the echelon/upper-triangular factor produced by elimination.",
  },
  {
    prompt: "To solve $Ax=b$ using $A=LU$, first solve:",
    options: ["$Ux=b$", "$Ly=b$", "$Lx=U$"],
    answer: "B",
    explanation: "Forward substitution solves for the intermediate vector $y$.",
  },
  {
    prompt: "After solving $Ly=b$, the second step is:",
    options: ["$Lx=y$", "$Uy=L$", "$Ux=y$"],
    answer: "C",
    explanation: "Backward substitution then recovers $x$.",
  },
  {
    prompt: "One advantage of LU factorization is that for many right-hand sides with the same $A$:",
    options: ["The factorization can be reused", "The matrix must be refactored every time", "No substitutions are required"],
    answer: "A",
    explanation: "Factor $A$ once, then use forward/back substitution for each new right-hand side.",
  },
];

export const LA_E_INTRO_QUIZ = [
  {
    prompt: "If $A\\mathbf{v}=3\\mathbf{v}$ with $\\mathbf{v}\\ne\\mathbf{0}$, then $\\mathbf{v}$ is an eigenvector with eigenvalue:",
    options: ["$3$", "$0$", "$1$"],
    answer: "A",
    explanation: "Definition: $A\\mathbf{v}=\\lambda\\mathbf{v}$.",
  },
  {
    prompt: "For $A=\\begin{pmatrix}2&0\\\\0&5\\end{pmatrix}$, an eigenvalue is:",
    options: ["$1$", "$7$", "$2$"],
    answer: "C",
    explanation: "Diagonal entries are the eigenvalues.",
  },
  {
    prompt: "Does $\\mathbf{v}=\\begin{pmatrix}1\\\\0\\end{pmatrix}$ satisfy $A\\mathbf{v}=\\lambda\\mathbf{v}$ for $A=\\begin{pmatrix}4&1\\\\0&2\\end{pmatrix}$?",
    options: ["Yes, with $\\lambda=2$", "Yes, with $\\lambda=4$", "No"],
    answer: "B",
    explanation: "$A\\mathbf{v}=\\begin{pmatrix}4\\\\0\\end{pmatrix}=4\\mathbf{v}$.",
  },
  {
    prompt: "Eigenvalue $0$ for $A$ means:",
    options: ["$A$ is diagonal", "$A$ is singular", "$A$ is the identity"],
    answer: "B",
    explanation: "$A\\mathbf{v}=\\mathbf{0}$ for some nonzero $\\mathbf{v}$.",
  },
  {
    prompt: "If $\\mathbf{v}$ is an eigenvector, then $5\\mathbf{v}$ is:",
    options: ["An eigenvector for eigenvalue $5$", "Never an eigenvector", "An eigenvector for the same eigenvalue"],
    answer: "C",
    explanation: "Any nonzero multiple remains an eigenvector.",
  },
  {
    prompt: "Eigenvalues of the triangular matrix $\\begin{pmatrix}3&7\\\\0&-1\\end{pmatrix}$ are:",
    options: ["$3$ and $-1$", "$7$ and $0$", "$3$ and $7$"],
    answer: "A",
    explanation: "For triangular matrices, eigenvalues sit on the diagonal.",
  },
  {
    prompt: "The eigenspace for $\\lambda$ is:",
    options: ["$\\operatorname{Nul}A$ only when $\\lambda=1$", "$\\operatorname{Col}A$", "$\\operatorname{Nul}(A-\\lambda I)$"],
    answer: "C",
    explanation: "Solve $(A-\\lambda I)\\mathbf{v}=\\mathbf{0}$.",
  },
  {
    prompt: "Can $\\mathbf{0}$ be called an eigenvector?",
    options: ["No; eigenvectors must be nonzero", "Yes; only for $\\lambda=0$", "Yes; for every $\\lambda$"],
    answer: "A",
    explanation: "The definition excludes the zero vector.",
  },
  {
    prompt: "If $A\\begin{pmatrix}1\\\\1\\end{pmatrix}=\\begin{pmatrix}2\\\\2\\end{pmatrix}$, the corresponding eigenvalue is:",
    options: ["$1$", "$2$", "$0$"],
    answer: "B",
    explanation: "The image is $2$ times the vector.",
  },
  {
    prompt: "For $I_2$, every nonzero vector is an eigenvector with eigenvalue:",
    options: ["$0$", "$1$", "$2$"],
    answer: "B",
    explanation: "$I\\mathbf{v}=1\\cdot\\mathbf{v}$.",
  },
  {
    prompt: "A $2\\times 2$ matrix can have at most how many distinct eigenvalues?",
    options: ["$4$", "$1$ always", "$2$"],
    answer: "C",
    explanation: "Degree of the characteristic polynomial is $2$.",
  },
  {
    prompt: "If $A\\mathbf{v}=\\lambda\\mathbf{v}$, then $A(2\\mathbf{v})$ equals:",
    options: ["$\\lambda(2\\mathbf{v})$", "$2\\lambda^2\\mathbf{v}$", "$\\mathbf{v}$"],
    answer: "A",
    explanation: "Linearity preserves the same $\\lambda$.",
  },
  {
    prompt: "For $A=\\begin{pmatrix}0&1\\\\0&0\\end{pmatrix}$, a nonzero eigenvector for $\\lambda=0$ is:",
    options: ["$\\begin{pmatrix}1\\\\1\\end{pmatrix}$", "$\\begin{pmatrix}0\\\\1\\end{pmatrix}$", "$\\begin{pmatrix}1\\\\0\\end{pmatrix}$"],
    answer: "C",
    explanation: "$A\\begin{pmatrix}1\\\\0\\end{pmatrix}=\\mathbf{0}$.",
  },
  {
    prompt: "Eigenvectors for distinct eigenvalues of a real matrix are:",
    options: ["Linearly independent", "Always orthogonal", "Always parallel"],
    answer: "A",
    explanation: "Standard independence theorem for distinct eigenvalues.",
  },
  {
    prompt: "If every vector is an eigenvector of $A$, then $A$ must be:",
    options: ["Nilpotent", "A scalar multiple of $I$", "Singular"],
    answer: "B",
    explanation: "Only scalar matrices act by the same factor on every vector.",
  },

  {
    prompt: "A nonzero vector $v$ is an eigenvector of $A$ when:",
    options: ["$Av=\\lambda v$ for some scalar $\\lambda$", "$Av=0$ only", "$A+v=\\lambda$"],
    answer: "A",
    explanation: "An eigenvector keeps its direction under the transformation, up to scaling.",
  },
  {
    prompt: "The zero vector is excluded from the definition of eigenvector because:",
    options: ["It has norm one", "It would satisfy $A0=\\lambda0$ for every scalar $\\lambda$", "It cannot be multiplied by a matrix"],
    answer: "B",
    explanation: "Allowing zero would make every scalar look like an eigenvalue.",
  },
  {
    prompt: "If $Av=3v$ with $v\\neq0$, then the eigenvalue associated with $v$ is:",
    options: ["$v$", "$0$", "$3$"],
    answer: "C",
    explanation: "The scalar multiplying the unchanged direction is the eigenvalue.",
  },
  {
    prompt: "Eigenvalues are defined directly for:",
    options: ["Square matrices", "Only row vectors", "Any rectangular matrix without modification"],
    answer: "A",
    explanation: "The equation $Av=\\lambda v$ requires input and output vectors in the same space.",
  },
  {
    prompt: "The eigenspace for eigenvalue $\\lambda$ is:",
    options: ["$\\operatorname{Col}(A+\\lambda I)$", "$\\operatorname{Nul}(A-\\lambda I)$", "$\\operatorname{Row}(A)$"],
    answer: "B",
    explanation: "Eigenvectors satisfy $(A-\\lambda I)v=0$.",
  },
];

export const LA_E_CHAR_QUIZ = [
  {
    prompt: "The characteristic polynomial of $A$ is:",
    options: ["$\\operatorname{tr}(A-\\lambda I)$", "$\\det(A+\\lambda I)$", "$\\det(A-\\lambda I)$"],
    answer: "C",
    explanation: "Roots are the eigenvalues.",
  },
  {
    prompt: "For $A=\\begin{pmatrix}1&2\\\\0&3\\end{pmatrix}$, eigenvalues are:",
    options: ["$1$ and $3$", "$2$ and $0$", "$1$ and $2$"],
    answer: "A",
    explanation: "Triangular: diagonal entries.",
  },
  {
    prompt: "Characteristic polynomial of $\\begin{pmatrix}0&1\\\\-2&-3\\end{pmatrix}$ is:",
    options: ["$\\lambda^2+3\\lambda-2$", "$\\lambda^2+3\\lambda+2$", "$\\lambda^2-3\\lambda+2$"],
    answer: "B",
    explanation: "$\\det\\begin{pmatrix}-\\lambda&1\\\\-2&-3-\\lambda\\end{pmatrix}=\\lambda^2+3\\lambda+2$.",
  },
  {
    prompt: "Sum of eigenvalues of $\\begin{pmatrix}4&1\\\\2&5\\end{pmatrix}$ equals:",
    options: ["$20$", "$9$", "$3$"],
    answer: "B",
    explanation: "Trace equals $4+5=9$.",
  },
  {
    prompt: "Product of eigenvalues of $\\begin{pmatrix}2&0\\\\0&3\\end{pmatrix}$ equals:",
    options: ["$6$", "$1$", "$5$"],
    answer: "A",
    explanation: "Determinant equals the product of eigenvalues.",
  },
  {
    prompt: "Algebraic multiplicity of $\\lambda$ is:",
    options: ["Always $1$", "The dimension of the eigenspace", "Its multiplicity as a root of the characteristic polynomial"],
    answer: "C",
    explanation: "Definition of algebraic multiplicity.",
  },
  {
    prompt: "Rotation by $90^\\circ$, matrix $\\begin{pmatrix}0&-1\\\\1&0\\end{pmatrix}$, has eigenvalues:",
    options: ["$\\pm 1$", "$\\pm i$", "$0$ (double)"],
    answer: "B",
    explanation: "Characteristic polynomial $\\lambda^2+1=0$.",
  },
  {
    prompt: "For $A=\\begin{pmatrix}5&0\\\\0&5\\end{pmatrix}$, the characteristic polynomial is:",
    options: ["$(5-\\lambda)^2$", "$\\lambda^2-5$", "$(5-\\lambda)$"],
    answer: "A",
    explanation: "Both eigenvalues are $5$.",
  },
  {
    prompt: "If $\\det A=0$, then $\\lambda=0$ is:",
    options: ["Always simple", "Never an eigenvalue", "An eigenvalue"],
    answer: "C",
    explanation: "Singular matrices have $0$ as an eigenvalue.",
  },
  {
    prompt: "Expanding $\\det\\begin{pmatrix}2-\\lambda&1\\\\0&3-\\lambda\\end{pmatrix}$ gives:",
    options: ["$(2-\\lambda)+(3-\\lambda)$", "$(2-\\lambda)(3-\\lambda)$", "$\\lambda^2-5$"],
    answer: "B",
    explanation: "Upper triangular factorisation.",
  },
  {
    prompt: "A $3\\times 3$ matrix has characteristic polynomial of degree:",
    options: ["$2$", "$9$", "$3$"],
    answer: "C",
    explanation: "Degree equals the matrix size.",
  },
  {
    prompt: "Eigenvalues of $\\begin{pmatrix}1&4\\\\2&3\\end{pmatrix}$ solve:",
    options: ["$(1-\\lambda)(3-\\lambda)-8=0$", "$\\lambda^2=0$", "$(1-\\lambda)(3-\\lambda)+8=0$"],
    answer: "A",
    explanation: "$\\det=\\lambda^2-4\\lambda-5$; equivalently $(1-\\lambda)(3-\\lambda)-8=0$.",
  },
  {
    prompt: "If eigenvalues are $2,2,5$, the trace is:",
    options: ["$20$", "$5$", "$9$"],
    answer: "C",
    explanation: "Sum with multiplicity: $2+2+5=9$.",
  },
  {
    prompt: "Complex conjugate eigenvalues appear for real matrices because:",
    options: ["Determinants are imaginary", "Real characteristic polynomials have conjugate roots when complex", "Trace must be zero"],
    answer: "B",
    explanation: "Coefficients are real.",
  },
  {
    prompt: "For $A=\\begin{pmatrix}0&-2\\\\1&3\\end{pmatrix}$, $\\det(A-\\lambda I)$ equals:",
    options: ["$\\lambda^2-3\\lambda+2$", "$\\lambda^2-3\\lambda-2$", "$\\lambda^2+3\\lambda+2$"],
    answer: "A",
    explanation: "$\\det\\begin{pmatrix}-\\lambda&-2\\\\1&3-\\lambda\\end{pmatrix}=\\lambda(\\lambda-3)+2=\\lambda^2-3\\lambda+2$.",
  },
  {
    prompt: "If $\\lambda=4$ has algebraic multiplicity $3$ and $\\dim\\operatorname{Nul}(A-4I)=2$, its geometric multiplicity is:",
    options: ["$3$", "$2$", "$1$"],
    answer: "B",
    explanation: "Geometric multiplicity is the dimension of the eigenspace, so it is $2$.",
  },
  {
    prompt: "For every eigenvalue $\\lambda$, which relation must hold?",
    options: ["$1\\le GM(\\lambda)\\le AM(\\lambda)$", "$GM(\\lambda)>AM(\\lambda)$", "$GM(\\lambda)=AM(\\lambda)$ always"],
    answer: "A",
    explanation: "An eigenspace is nonzero and its dimension cannot exceed the root's algebraic multiplicity.",
  },
  {
    prompt: "For $A=\\operatorname{diag}(2,2,5)$, the eigenvalue $2$ has:",
    options: ["$AM=1,\\ GM=2$", "$AM=2,\\ GM=1$", "$AM=2,\\ GM=2$"],
    answer: "C",
    explanation: "$2$ is a double root and its eigenspace is $\\operatorname{Span}\\{e_1,e_2\\}$.",
  },


  {
    prompt: "For a $2\\times2$ matrix, the characteristic polynomial has degree:",
    options: ["$1$", "$2$", "$4$"],
    answer: "B",
    explanation: "The characteristic polynomial of an $n\\times n$ matrix has degree $n$.",
  },
  {
    prompt: "For triangular matrix $A$, its eigenvalues are:",
    options: ["Its row sums only", "Always zero", "Its diagonal entries"],
    answer: "C",
    explanation: "The characteristic determinant of a triangular matrix is the product of diagonal terms $a_{ii}-\\lambda$.",
  },
];

export const LA_E_DIAG_QUIZ = [
  {
    prompt: "Diagonalization means writing $A$ as:",
    options: ["$P+D$", "$PDP^{-1}$ with $D$ diagonal", "$A^T A$"],
    answer: "B",
    explanation: "Similarity to a diagonal matrix.",
  },
  {
    prompt: "A $2\\times 2$ matrix with two independent eigenvectors is:",
    options: ["Defective", "Singular", "Diagonalizable"],
    answer: "C",
    explanation: "Enough independent eigenvectors to form $P$.",
  },
  {
    prompt: "If geometric multiplicity is less than algebraic multiplicity for some $\\lambda$, the matrix is:",
    options: ["Not diagonalizable (defective for that $\\lambda$)", "Orthogonally diagonalizable", "Invertible"],
    answer: "A",
    explanation: "Shortage of eigenvectors blocks diagonalization.",
  },
  {
    prompt: "For $A=\\begin{pmatrix}2&0\\\\0&3\\end{pmatrix}$, one may take $P=I$ and $D=$:",
    options: ["$\\begin{pmatrix}2&0\\\\0&3\\end{pmatrix}$", "$\\begin{pmatrix}1&0\\\\0&1\\end{pmatrix}$", "$\\begin{pmatrix}0&2\\\\3&0\\end{pmatrix}$"],
    answer: "A",
    explanation: "Already diagonal.",
  },
  {
    prompt: "If $A=PDP^{-1}$, then $A^k$ equals:",
    options: ["$P^k D P^{-1}$", "$D^k$ alone", "$PD^k P^{-1}$"],
    answer: "C",
    explanation: "Powers pass onto the diagonal factor.",
  },
  {
    prompt: "Symmetric real matrices are:",
    options: ["Always defective", "Orthogonally diagonalizable", "Never diagonalizable"],
    answer: "B",
    explanation: "Spectral theorem for real symmetric matrices.",
  },
  {
    prompt: "Building $P$ means placing:",
    options: ["Eigenvalues on the diagonal of $P$", "Independent eigenvectors as columns", "Rows of $A$"],
    answer: "B",
    explanation: "$AP=PD$ uses eigenvector columns.",
  },
  {
    prompt: "The matrix $\\begin{pmatrix}0&1\\\\0&0\\end{pmatrix}$ is:",
    options: ["Not diagonalizable", "Orthogonal", "Diagonalizable with $D=0$"],
    answer: "A",
    explanation: "Only a one-dimensional eigenspace for $\\lambda=0$.",
  },
  {
    prompt: "If $D=\\begin{pmatrix}2&0\\\\0&2\\end{pmatrix}$ and $P$ is invertible, $PDP^{-1}$ equals:",
    options: ["$D$ only when $P=I$", "$P$", "$2I$"],
    answer: "C",
    explanation: "$P(2I)P^{-1}=2I$.",
  },
  {
    prompt: "Geometric multiplicity is:",
    options: ["The exponent of $(t-\\lambda)$ in $\\det(A-tI)$", "Always equal to $n$", "$\\dim\\operatorname{Nul}(A-\\lambda I)$"],
    answer: "C",
    explanation: "Dimension of the eigenspace.",
  },
  {
    prompt: "For diagonalizable $A$ with eigenvalues $1$ and $4$, $A^2$ has eigenvalues:",
    options: ["$1$ and $4$", "$1$ and $16$", "$2$ and $8$"],
    answer: "B",
    explanation: "Eigenvalues of $A^k$ are $\\lambda^k$.",
  },
  {
    prompt: "A sufficient condition for a $3\\times 3$ matrix to be diagonalizable is:",
    options: ["Three distinct eigenvalues", "Trace zero", "Determinant zero"],
    answer: "A",
    explanation: "Distinct eigenvalues give independent eigenvectors.",
  },
  {
    prompt: "If $P=\\begin{pmatrix}1&1\\\\0&1\\end{pmatrix}$ and $D=\\begin{pmatrix}2&0\\\\0&3\\end{pmatrix}$, then $A=PDP^{-1}$ has eigenvalues:",
    options: ["$1$ and $1$", "$2$ and $3$", "$0$ and $1$"],
    answer: "B",
    explanation: "Similar matrices share eigenvalues; here those of $D$.",
  },
  {
    prompt: "Orthogonal diagonalization uses $P$ with:",
    options: ["$P^T P=I$", "$\\det P=0$", "$P=D$"],
    answer: "A",
    explanation: "Orthonormal eigenvector basis.",
  },
  {
    prompt: "Computing $A^{10}$ via diagonalization is efficient when:",
    options: ["$n$ is infinite", "$A$ is singular only", "$A$ is diagonalizable so $D^{10}$ is cheap"],
    answer: "C",
    explanation: "Diagonal powers are entrywise.",
  },
  {
    prompt: "If the characteristic polynomial splits, $A$ is diagonalizable exactly when:",
    options: ["$GM(\\lambda)=AM(\\lambda)$ for every eigenvalue", "$\\det A=1$", "All eigenvalues are positive"],
    answer: "A",
    explanation: "Matching algebraic and geometric multiplicities supplies a full eigenbasis.",
  },
  {
    prompt: "For a real symmetric matrix $A$, the Spectral Theorem guarantees:",
    options: ["$A=PDP^{-1}$ for an arbitrary invertible $P$", "$A=Q\\Lambda Q^T$ for an orthogonal $Q$", "$A=LU$ with $L=U^T$"],
    answer: "B",
    explanation: "Real symmetric matrices admit an orthonormal eigenbasis, giving orthogonal diagonalization.",
  },
  {
    prompt: "If $A=Q\\Lambda Q^T$ has orthonormal eigenvectors $q_i$, its spectral decomposition is:",
    options: ["$A=\\sum_i q_iq_i^T$", "$A=\\sum_i \\lambda_i^2 q_iq_i^T$", "$A=\\sum_i \\lambda_i q_iq_i^T$"],
    answer: "C",
    explanation: "Each eigenvalue weights the orthogonal projector $q_iq_i^T$ onto its eigenvector direction.",
  },


  {
    prompt: "A matrix $A$ is diagonalizable when it has:",
    options: ["Only one eigenvalue", "Determinant zero", "$n$ linearly independent eigenvectors"],
    answer: "C",
    explanation: "Those eigenvectors form the columns of the change-of-basis matrix $P$.",
  },
  {
    prompt: "If $A=PDP^{-1}$, then $A^k$ equals:",
    options: ["$PD^kP^{-1}$", "$P^kDP^{-k}$", "$D^k$ in the original basis"],
    answer: "A",
    explanation: "Repeated factors $P^{-1}P$ cancel in powers of the diagonalization.",
  },
];

export const LA_E_APPS_QUIZ = [
  {
    prompt: "For a Markov transition matrix, long-run behavior is governed by:",
    options: ["The eigenvalue $1$ and its eigenvector (steady state)", "Only negative eigenvalues", "The trace alone"],
    answer: "A",
    explanation: "Stochastic matrices have dominant eigenvalue $1$ under mild conditions.",
  },
  {
    prompt: "A discrete system $\\mathbf{x}_{k+1}=A\\mathbf{x}_k$ tends to $\\mathbf{0}$ if all eigenvalues satisfy:",
    options: ["$\\lambda=0$ only", "$\\lambda>1$", "$|\\lambda|<1$"],
    answer: "C",
    explanation: "Each mode scales by $\\lambda^k\\to 0$ when $|\\lambda|<1$.",
  },
  {
    prompt: "The power method primarily approximates:",
    options: ["The smallest singular value only", "The dominant eigenvalue and a corresponding eigenvector", "The determinant"],
    answer: "B",
    explanation: "Repeated multiplication amplifies the largest $|\\lambda|$ direction.",
  },
  {
    prompt: "For $\\mathbf{x}'=A\\mathbf{x}$, solutions behave like $e^{\\lambda t}\\mathbf{v}$ along eigenvectors. Stability of the origin needs:",
    options: ["All $\\lambda>0$", "All $\\operatorname{Re}(\\lambda)<0$", "Trace positive"],
    answer: "B",
    explanation: "Negative real parts give exponential decay.",
  },
  {
    prompt: "Principal component directions are eigenvectors of:",
    options: ["Any singular matrix", "A Markov matrix only", "A covariance (or correlation) matrix"],
    answer: "C",
    explanation: "PCA diagonalizes the covariance.",
  },
  {
    prompt: "If the dominant eigenvalue of a positive transition structure is $1$ and others have $|\\lambda|<1$, iterates approach:",
    options: ["A steady-state multiple of the dominant eigenvector", "Oscillation with growing amplitude", "Zero always"],
    answer: "A",
    explanation: "Transient modes die out.",
  },
  {
    prompt: "Vibrational modes of a linear mechanical model often correspond to:",
    options: ["Only the determinant", "Row sums of $A$", "Eigenpairs of a stiffness/mass generalized problem"],
    answer: "C",
    explanation: "Modal analysis is an eigenvalue problem.",
  },
  {
    prompt: "If $A$ has eigenvalues $2$ and $1/2$, then $A^k\\mathbf{v}$ for a generic $\\mathbf{v}$ grows like:",
    options: ["$k$", "$2^k$ along the dominant mode", "$(1/2)^k$"],
    answer: "B",
    explanation: "The larger magnitude eigenvalue dominates.",
  },
  {
    prompt: "A $2\\times 2$ rotation by a non-multiple of $180^\\circ$ has $|\\lambda|=1$ and complex $\\lambda$, so powers:",
    options: ["Rotate without radial growth", "Tend to infinity", "Tend to zero"],
    answer: "A",
    explanation: "Eigenvalues lie on the unit circle.",
  },
  {
    prompt: "In population projection matrices, the long-term growth rate is:",
    options: ["The nullity", "The number of states", "The dominant eigenvalue"],
    answer: "C",
    explanation: "Leslie/Lefkovitch models use the spectral radius.",
  },
  {
    prompt: "If all $|\\lambda|>1$, the discrete iteration $\\mathbf{x}\\mapsto A\\mathbf{x}$ typically:",
    options: ["Diverges for nonzero starts", "Converges to zero", "Stays on a sphere"],
    answer: "A",
    explanation: "Every mode amplifies.",
  },
  {
    prompt: "Diagonalization helps solve $\\mathbf{x}'=A\\mathbf{x}$ by:",
    options: ["Forcing $A=0$", "Decoupling into scalar ODEs in eigen-coordinates", "Removing all eigenvalues"],
    answer: "B",
    explanation: "Change of basis $\\mathbf{x}=P\\mathbf{y}$.",
  },
  {
    prompt: "For a symmetric covariance matrix, PCA axes are:",
    options: ["Orthogonal eigenvectors", "Always the standard basis", "Singular vectors of a rectangular data matrix only, never of covariance"],
    answer: "A",
    explanation: "Real symmetric eigenbases can be chosen orthonormal.",
  },
  {
    prompt: "If $\\lambda=1$ is simple and dominant for a stochastic matrix with positive entries, the steady state is:",
    options: ["Every vector", "Unique up to scaling (and can be normalized to a probability vector)", "Only $\\mathbf{0}$"],
    answer: "B",
    explanation: "Perron–Frobenius type conclusions.",
  },
  {
    prompt: "The spectral radius $\\rho(A)<1$ implies that $A^k\\to 0$, which is used to conclude:",
    options: ["That $\\det A=1$", "That $A$ is orthogonal", "Asymptotic stability of $\\mathbf{x}\\mapsto A\\mathbf{x}$"],
    answer: "C",
    explanation: "All modes decay.",
  },

  {
    prompt: "For the recurrence $x_{k+1}=Ax_k$, an eigenvector initial state $x_0=v$ evolves as:",
    options: ["$x_k=\\lambda^k v$", "$x_k=kv$", "$x_k=A+kv$"],
    answer: "A",
    explanation: "Each application of $A$ multiplies the eigenvector by $\\lambda$.",
  },
  {
    prompt: "In a discrete dynamical system, an eigenvalue with $|\\lambda|<1$ corresponds to a mode that:",
    options: ["Grows without bound in magnitude", "Decays toward zero", "Never changes magnitude"],
    answer: "B",
    explanation: "Repeated powers $|\\lambda|^k$ tend to zero.",
  },
  {
    prompt: "An eigenvalue with $|\\lambda|>1$ in a discrete iteration corresponds to a mode that:",
    options: ["Always decays", "Is automatically zero", "Grows in magnitude"],
    answer: "C",
    explanation: "Repeated multiplication by a factor with magnitude greater than one amplifies that mode.",
  },
  {
    prompt: "For a symmetric matrix, eigenvectors associated with distinct eigenvalues can be chosen:",
    options: ["Orthogonal", "Parallel", "Nonreal only"],
    answer: "A",
    explanation: "The spectral theorem provides an orthonormal eigenbasis for real symmetric matrices.",
  },
  {
    prompt: "Principal component analysis uses eigenvectors of a covariance matrix to identify:",
    options: ["Only the sample mean", "Directions of greatest variance", "Random directions independent of the data"],
    answer: "B",
    explanation: "Eigenvectors order directions according to covariance eigenvalues, which measure variance along those directions.",
  },
];
